import { useEffect, useState } from "react";
import { AutomationRepository } from "@/repositories/automation";
import { useStore } from "@/stores/useStore";
import { Automation, CreateAutomationDto } from "@/types/v2/Automation";
import { FirestoreError } from "firebase/firestore";
import { Spinner } from "@/components/Common/Spinner";
import { AutomationItem } from "@/components/Automations/AutomationItem";
import { SupportLink } from "@/components/Common/SupportLink";
import { AutomationDialog } from "@/components/Automations/AutomationDialog";
import { Button } from "@/components/Base/Button";
import { DownloadIcon } from "lucide-react";
import { useDynamicOptions } from "@/hooks/useDynamicOptions";
import {
  buildAutomationExport,
  downloadAutomationExport,
} from "@/lib/automationExport";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { ImportAutomationsDialog } from "@/components/Automations/ImportAutomationsDialog";
import { useAutomationBuilderAnalytics } from "@/hooks/useAutomationBuilderAnalytics";

export const AutomationsConfiguration = () => {
  const { shopId } = useStore();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FirestoreError | null>(null);
  const { generateOptions } = useDynamicOptions();
  const { logExportUsed } = useAutomationBuilderAnalytics();

  useEffect(() => {
    const unsubscribe = shopId
      ? AutomationRepository.subscribeAll(
          shopId,
          (data) => {
            setLoading(true);
            setAutomations(data ?? []);
            setError(null);
            setLoading(false);
          },
          (error) => setError(error)
        )
      : () => {};
    return () => unsubscribe();
  }, [shopId]);

  const handleCreate = async (automation: CreateAutomationDto) => {
    if (!shopId) return;
    setLoading(true);
    try {
      await AutomationRepository.create(shopId, automation);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleExportAll = () => {
    if (!shopId) return;
    try {
      const exportFile = buildAutomationExport(
        automations,
        shopId,
        generateOptions
      );
      downloadAutomationExport(
        exportFile,
        `automations-${shopId}-${Date.now()}.json`
      );
      showSuccessToast("Automations exported successfully");
      logExportUsed("all");
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to export automations");
    }
  };

  if (loading || !shopId) return <Spinner />;

  if (error) return <div>{error.message}</div>;

  return (
    <div className="space-y-2 overflow-y-auto pl-4">
      <h2 className="pt-2 text-lg font-bold">About Automations</h2>
      <p>
        Automations are an integral feature of ShopHero Dashboard. They empower
        you to automate numerous manual tasks related to card management,
        enabling an optimised, efficient and add a touch of
        &#39;intelligence&#39; to your workflow. With automations, you can
        configure a variety of triggers and conditions that when met, activate
        certain actions on the cards.
      </p>
      <p>
        For instance, an automation can be set up such that when a card is moved
        to a specific container, the card&#39;s color changes automatically.
        This allows for visual cues to be set up, aiding in better organization
        and understanding of tasks at a simple glance.
      </p>
      <p>
        Additionally, automations are designed to be flexible and intelligent.
        It includes conditional checks in triggers, permitting precision and
        control. For example, you may want a card action to only trigger when
        the card is moved to a specific container named &#39;INSPECTION&#39;.
        The automatic checks will validate this condition before executing the
        action.
      </p>
      <p>
        In essence, these automations are designed to provide an automated,
        precise, and intelligent system, keeping you in control while maximizing
        efficiency
      </p>
      <p>
        Need help setting up automations? Click <SupportLink>here</SupportLink>
      </p>
      <div className="flex items-center gap-2">
        <AutomationDialog onSubmit={handleCreate} />
        <Button
          variant={"outline"}
          onClick={handleExportAll}
          disabled={automations.length === 0}
        >
          <DownloadIcon className="mr-2 h-4 w-4" />
          Export All
        </Button>
        <ImportAutomationsDialog shopId={shopId} automations={automations} />
      </div>
      <h2 className="pt-8 text-lg font-bold">Current Automations</h2>
      {automations.map((automation) => (
        <AutomationItem
          automation={automation}
          key={automation.id}
          shopId={shopId}
          canMoveDown={automations.indexOf(automation) < automations.length - 1}
          canMoveUp={automations.indexOf(automation) > 0}
        />
      ))}
    </div>
  );
};
